import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { generatePbiConnectionUrl } from '../../utils/adminUtils';
import { formatDateWithMonthName } from '../../utils/dateUtils';
import styles from './AdminTenantDetails.module.css';

const AdminTenantDetails: React.FC = () => {
  const { selectedTenant, tenantUsers, loadingUsers } = useAdmin();
  const [showToken, setShowToken] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const handleCopyPbiUrl = async () => {
    if (!selectedTenant) return;
    
    const pbiUrl = generatePbiConnectionUrl(selectedTenant.token);
    
    try {
      await navigator.clipboard.writeText(pbiUrl);
      setCopySuccess('PBI URL copied!');
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      setCopySuccess('Failed to copy URL');
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  if (!selectedTenant) {
    return (
      <div className={styles['admin-tenant-details']}>
        <div className={styles['no-selection']}>
          <h3>Select a Tenant</h3>
          <p>Choose a tenant from the list to view details and users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['admin-tenant-details']}>
      <div className={styles['tenant-header']}>
        <h2>{selectedTenant.name}</h2>
        <div className={styles['tenant-info']}>
          <p><strong>Tenant ID:</strong> {selectedTenant.id}</p>
          <p><strong>Created:</strong> {new Date(selectedTenant.created_at).toLocaleDateString()}</p>
          <p><strong>Last Updated:</strong> {new Date(selectedTenant.updated_at).toLocaleDateString()}</p>
          <div className={styles['token-section']}>
            <p><strong>Tenant Token:</strong></p>
            <div className={styles['token-display']}>
              <span className={styles['token-value']}>
                {showToken ? selectedTenant.token : '••••••••••••••••••••••••••••••••'}
              </span>
              <button 
                type="button"
                className={styles['token-toggle']}
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          
          <div className={styles['pbi-section']}>
            <p><strong>Power BI Connection:</strong></p>
            <div className={styles['pbi-info']}>
              <p className={styles['pbi-description']}>
                Use this link to connect Power BI to the tenant's expense data. 
                Data includes expenses from the beginning of last year.
              </p>
              <div className={styles['pbi-links']}>
                <div className={styles['pbi-link-item']}>
                  <button 
                    type="button"
                    className={styles['pbi-copy-btn']}
                    onClick={handleCopyPbiUrl}
                  >
                    Copy PBI Link
                  </button>
                </div>
              </div>
              {copySuccess && (
                <div className={styles['copy-success']}>
                  {copySuccess}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles['tenant-stats']}>
        <div className={styles['stat-card']}>
          <h4>Total Users</h4>
          <div className={styles['stat-value']}>{selectedTenant.user_count}</div>
        </div>
        <div className={styles['stat-card']}>
          <h4>WhatsApp Users</h4>
          <div className={styles['stat-value']}>{selectedTenant.whatsapp_users}</div>
        </div>
        <div className={styles['stat-card']}>
          <h4>Email Users</h4>
          <div className={styles['stat-value']}>{selectedTenant.email_users}</div>
        </div>
      </div>

      <div className={styles['users-section']}>
        <h3>
          Users 
          {loadingUsers ? (
            <span className={styles['loading-indicator']}>
              <div className={styles['loading-dot']}></div>
              <div className={styles['loading-dot']}></div>
              <div className={styles['loading-dot']}></div>
            </span>
          ) : (
            ` (${tenantUsers.length})`
          )}
        </h3>
        
        {loadingUsers ? (
          <div className={styles['users-table-skeleton']}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>WhatsApp Name</th>
                  <th>Status</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td><div className={styles['skeleton-text']}></div></td>
                    <td><div className={styles['skeleton-text']}></div></td>
                    <td><div className={styles['skeleton-text']}></div></td>
                    <td><div className={styles['skeleton-text']}></div></td>
                    <td><div className={styles['skeleton-badge']}></div></td>
                    <td><div className={styles['skeleton-text']}></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles['users-table']}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>WhatsApp Name</th>
                  <th>Status</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {tenantUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email || '-'}</td>
                    <td>{user.phone_number || '-'}</td>
                    <td>{user.whatsapp_name || '-'}</td>
                    <td>
                      <span className={`${styles.status} ${user.is_active ? styles.active : styles.inactive}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {user.last_login 
                        ? formatDateWithMonthName(user.last_login)
                        : 'Never'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTenantDetails;
